<!--
  이 Vue 컴포넌트는 사용자 로그인을 위한 화면을 구성하며,
  이메일과 비밀번호를 입력받아 인증 API를 호출하고 로그인 상태를 설정합니다.
  로그인 성공 시 대시보드로 리디렉션되며, 실패 시 오류 메시지를 사용자에게 표시합니다.
  또한, 로딩 상태 관리와 가입 링크를 통해 사용자 전환도 지원합니다.
-->

<template>
  <div class="login-container">
    <!-- 🔧 UI/UX 개선: 흰색 배경 적용 -->
    <AnimatedBackground backgroundColor="white" />
    
    <div class="login-box">
      <div class="login-header">
        <h1 class="login-title">AITRA</h1>
        <p class="login-subtitle">Sense Language, Sprit Heard</p>
      </div>
      <form @submit.prevent="handleLogin" class="login-form">
        <div class="form-group">
          <label for="email">Email</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            class="form-input"
            required
          >
        </div>
        <div class="form-group">
          <label for="password">Password</label>
          <input 
            type="password" 
            id="password" 
            v-model="password" 
            class="form-input"
            required
          >
        </div>
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        <button type="submit" class="login-button" :disabled="isLoading">
          {{ isLoading ? 'Logging in...' : 'Login' }}
        </button>
      </form>
      <p class="signup-link">
        Don't have an account? 
        <router-link to="/signup" class="signup-link-button">Sign up</router-link>
      </p>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
// 🔧 UI/UX 개선: 애니메이션 배경 컴포넌트 import
import AnimatedBackground from '@/components/AnimatedBackground.vue';

// Vue Router 인스턴스
const router = useRouter();

// 로그인 함수 가져오기
const { login } = useAuth();

// 사용자 입력값 및 상태 변수
const email = ref('');
const password = ref('');
const isLoading = ref(false); // 로그인 처리 중 여부
const errorMessage = ref(''); // 로그인 실패 시 사용자에게 보여줄 메시지

/**
 * 로그인 핸들러
 * 이메일/비밀번호를 입력받아 로그인 요청을 수행하고 성공 시 대시보드로 이동
 */
const handleLogin = async () => {
  errorMessage.value = '';
  isLoading.value = true;
  const response = await login(email.value, password.value);
  if (response) {
      await router.push('/dashboard');
  } else {
    // 인증 실패
    errorMessage.value = '로그인에 실패했습니다. 이메일 또는 비밀번호가 올바르지 않습니다.';
  }
  isLoading.value = false;
};
</script>

<style scoped>
.login-container {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  /* 🔧 UI/UX 개선: 애니메이션 배경이 보이도록 배경 제거 */
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #f0f9ff 100%);
  padding: 2rem;
  position: relative;
  overflow: hidden;
}

.login-box {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 2.5rem;
  border-radius: 1rem;
  box-shadow: 0 20px 40px -10px rgba(0, 51, 160, 0.1),
              0 10px 20px -5px rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

.login-header {
  text-align: center;
  margin-bottom: 2.5rem;
}

.login-title {
  font-size: 3rem;
  font-weight: 700;
  background: linear-gradient(135deg, #0033a0, #8cb8e8, #0033a0);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 0.75rem;
  animation: fadeInUp 1s ease-out;
}

.login-subtitle {
  color: var(--secondary);
  font-size: 1rem;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  color: var(--foreground);
  font-weight: 500;
  font-size: 0.9rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  background: var(--card);
  transition: all 0.2s;
}

.form-input:focus {
  border-color: var(--primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(230, 0, 18, 0.08);
}

.error-message {
  color: var(--destructive);
  font-size: 0.9rem;
  margin: 0;
  text-align: center;
}

.login-button {
  width: 100%;
  padding: 0.875rem;
  background: var(--primary);
  color: var(--primary-foreground);
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(0, 51, 160, 0.15);
}

.login-button:hover:not(:disabled) {
  background: var(--primary-hover);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 51, 160, 0.25);
}

.login-button:disabled {
  background: var(--muted);
  cursor: not-allowed;
  box-shadow: none;
}

.signup-link {
  text-align: center;
  margin-top: 2rem;
  color: var(--muted-foreground);
  font-size: 0.9rem;
}

.signup-link-button {
  color: var(--primary);
  text-decoration: none;
  font-weight: 500;
  margin-left: 0.25rem;
  transition: all 0.2s;
}

.signup-link-button:hover {
  color: var(--primary-hover);
  text-decoration: underline;
}

/* 🔧 UI/UX 개선: 애니메이션 키프레임 추가 */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>